import React from 'react'
import { Link } from 'react-router-dom'
import { Shield, FileText, RefreshCw, Printer, Mail, Phone, MapPin } from 'lucide-react'
import Header from '../../components/Header'
import Footer from '../../components/Footer'

interface LegalLayoutProps {
  activeTab: 'privacy' | 'terms' | 'cancellation'
  title: string
  subtitle: string
  lastUpdated: string
  children: React.ReactNode
}

const tabs = [
  {
    id: 'privacy',
    label: 'Privacy Policy',
    to: '/privacy-policy',
    icon: Shield,
  },
  {
    id: 'terms',
    label: 'Terms of Service',
    to: '/terms-of-service',
    icon: FileText,
  },
  {
    id: 'cancellation',
    label: 'Cancellation Policy',
    to: '/cancellation-policy',
    icon: RefreshCw,
  },
] as const

export default function LegalLayout({
  activeTab,
  title,
  subtitle,
  lastUpdated,
  children,
}: LegalLayoutProps) {
  const handlePrint = () => {
    window.print()
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F9FBFA]">
      {/* Top Header */}
      <div className="relative z-30 border-b border-gray-100 bg-white px-4 sm:px-8">
        <Header />
      </div>

      {/* Hero / Banner */}
      <section className="relative overflow-hidden bg-[#1A252A] py-14 text-white lg:py-16">
        {/* Subtle decorative circles */}
        <div className="pointer-events-none absolute -right-20 -top-20 h-80 w-80 rounded-full bg-[#FF7027]/10 blur-3xl" />
        <div className="pointer-events-none absolute -left-20 -bottom-20 h-80 w-80 rounded-full bg-[#85B718]/10 blur-3xl" />

        <div className="relative z-10 mx-auto max-w-page px-5 sm:px-8">
          {/* Breadcrumb */}
          <div className="mb-4 flex items-center gap-2 font-host text-xs text-gray-400">
            <Link to="/" className="transition hover:text-white">
              Home
            </Link>
            <span>/</span>
            <span className="text-gray-300">Legal & Policies</span>
            <span>/</span>
            <span className="text-[#FF7027] font-medium">{title}</span>
          </div>

          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="font-host text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-[42px]">
                {title}
              </h1>
              <p className="mt-2 max-w-2xl font-host text-sm text-gray-300 sm:text-base">
                {subtitle}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <span className="rounded-full bg-white/10 px-3.5 py-1.5 font-host text-xs text-gray-300 backdrop-blur-sm">
                Effective: {lastUpdated}
              </span>
              <button
                type="button"
                onClick={handlePrint}
                className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3.5 py-1.5 font-host text-xs font-medium text-white transition hover:bg-white/20 active:scale-95"
                title="Print Policy Document"
              >
                <Printer className="h-3.5 w-3.5" />
                <span>Print</span>
              </button>
            </div>
          </div>

          {/* Policy Navigation Tabs */}
          <div className="mt-8 flex flex-wrap gap-2.5 border-t border-white/10 pt-6">
            {tabs.map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              return (
                <Link
                  key={tab.id}
                  to={tab.to}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 font-host text-xs sm:text-sm font-semibold transition-all active:scale-[0.98] ${
                    isActive
                      ? 'bg-[#FF7027] text-white shadow-md'
                      : 'bg-white/5 text-gray-300 hover:bg-white/15 hover:text-white'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </Link>
              )
            })}
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <main className="flex-1 py-12 lg:py-16">
        <div className="mx-auto max-w-page px-5 sm:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
            {/* Left / Primary Document */}
            <article className="rounded-2xl border border-gray-200/80 bg-white p-6 sm:p-10 shadow-sm">
              {children}
            </article>

            {/* Right Sidebar / Need Help & Quick Contact */}
            <aside className="space-y-6">
              {/* Official Company Card */}
              <div className="rounded-2xl border border-gray-200/80 bg-white p-6 shadow-sm">
                <h3 className="font-host text-base font-semibold text-gray-900">
                  Salzburg Cab Official
                </h3>
                <p className="mt-1 font-host text-xs leading-relaxed text-gray-500">
                  Licensed passenger transport service in Austria and Central Europe.
                </p>

                <div className="mt-5 space-y-3.5 border-t border-gray-100 pt-4 font-host text-xs text-gray-600">
                  <div className="flex items-start gap-2.5">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#FF7027]" />
                    <span>Rudolf Spängler Straße 10, 5020 Salzburg, Austria</span>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <Phone className="h-4 w-4 shrink-0 text-[#FF7027]" />
                    <a href="tel:+43720775053" className="hover:text-[#FF7027] transition">
                      +43 720 7750 53
                    </a>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <Phone className="h-4 w-4 shrink-0 text-[#FF7027]" />
                    <a href="tel:+436763347141" className="hover:text-[#FF7027] transition">
                      +43 676 334 7141
                    </a>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <Mail className="h-4 w-4 shrink-0 text-[#FF7027]" />
                    <a
                      href="mailto:office@salzburgcab.com"
                      className="hover:text-[#FF7027] transition"
                    >
                      office@salzburgcab.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Quick Inquiry Card */}
              <div className="rounded-2xl bg-gradient-to-br from-[#1B7E9F]/10 to-[#85B718]/10 border border-[#1B7E9F]/20 p-6">
                <h4 className="font-host text-sm font-semibold text-gray-900">
                  Have Questions?
                </h4>
                <p className="mt-1 font-host text-xs text-gray-600 leading-relaxed">
                  Our customer support team is available 24/7 to assist with your bookings, itinerary adjustments, and policy inquiries.
                </p>
                <div className="mt-4 flex flex-col gap-2">
                  <Link
                    to="/booking"
                    className="inline-flex h-9 items-center justify-center rounded-xl bg-[#FF7027] font-host text-xs font-semibold text-white transition hover:bg-[#E56E25] active:scale-95"
                  >
                    Book Transfer Now
                  </Link>
                  <a
                    href="mailto:office@salzburgcab.com?subject=Legal%20or%20Policy%20Inquiry"
                    className="inline-flex h-9 items-center justify-center rounded-xl border border-gray-300 bg-white font-host text-xs font-semibold text-gray-700 transition hover:bg-gray-50 active:scale-95"
                  >
                    Email Support Team
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  )
}
